// Connect a declared number to a measure, from the screen.
//
// WHY THIS EXISTS AS A SEPARATE THING FROM PASTING
//
// A paste records what a person says they own. Deciding which computed figure
// they are then judged against is a different act with a different owner, and it
// is the step that moves a number from "declared, not measured" to measured.
// Migration 0099 enforces that split server side: operator_metric_bind_v1 is
// admin only and refuses a blank rationale.
//
// It was missing from the screen, which made the split useless in practice: the
// outstanding list told you a number was not measured and gave you no way to
// change that. The handler existed and nothing in web/src called it.
//
// Two rules carried through from the migration, because a screen that hides a
// refusal is worse than one that never offered the action:
//
//   - Only measures that can answer at the number's own rhythm are offered. A
//     monthly-only measure cannot serve a number its owner checks weekly, and
//     the function refuses it, so it is not put in the list.
//   - A quarterly number cannot be measured at all yet. That is said out loud
//     here rather than shown as an empty dropdown.
import { useState } from 'react'
import { Link2 } from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Spinner } from '../../lib/shadcn/spinner'
import { useBindOperatorMetric, useUnbindOperatorMetric } from '../../hooks/backend/operator'
import type { Grain, MeasureRow, NumberStatusRow } from '../../utils/operator'

// The grain a declared cadence needs. Quarterly maps to null: operator_grain has
// no quarter, and mapping it onto month would render one month's figure on a row
// labelled quarterly.
function grainFor(cadence: NumberStatusRow['cadence']): Grain | null {
  if (cadence === 'daily') return 'day'
  if (cadence === 'weekly') return 'week'
  if (cadence === 'monthly' || cadence === null) return 'month'
  return null
}

export function BindNumber({
  number,
  measures,
  isAdmin,
  onDone,
}: {
  number: NumberStatusRow
  measures: MeasureRow[]
  isAdmin: boolean
  onDone: () => void
}) {
  const bindFn = useBindOperatorMetric()
  const unbindFn = useUnbindOperatorMetric()
  const [open, setOpen] = useState(false)
  const [measureKey, setMeasureKey] = useState('')
  const [rationale, setRationale] = useState('')
  const [error, setError] = useState<string | null>(null)

  const needed = grainFor(number.cadence)
  const eligible = needed === null ? [] : measures.filter((m) => m.grains.includes(needed))
  const chosen = eligible.find((m) => m.measure_key === measureKey) ?? null

  const run = async (fn: () => Promise<unknown>) => {
    setError(null)
    try {
      const res = (await fn()) as Record<string, unknown> | undefined
      if (res && res['ok'] === false) {
        setError(String(res['reason'] ?? 'It was not saved.'))
        return
      }
      setOpen(false)
      setRationale('')
      onDone()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'It was not saved.')
    }
  }

  if (number.is_bound) {
    return (
      <div className="mt-1.5 space-y-1 border-t border-border pt-1.5 text-xs">
        <div className="text-muted-foreground">
          Measured as <span className="font-medium text-foreground">{number.measure_label}</span>
          {number.measured_periods === 0 ? ', with no figures computed yet' : null}
        </div>
        {number.binding_rationale ? (
          <div className="italic text-muted-foreground">Why: {number.binding_rationale}</div>
        ) : null}
        {isAdmin ? (
          <button
            type="button"
            className="text-xs text-muted-foreground underline hover:text-foreground"
            onClick={() => {
              const reason = 'Disconnected from the Operators view'
              void run(() =>
                unbindFn.trigger({ metric_id: number.metric_id, reason }, { skipCache: true }),
              )
            }}
            disabled={unbindFn.loading}
          >
            {unbindFn.loading ? 'Disconnecting' : 'Disconnect this measure'}
          </button>
        ) : null}
        {error ? <div className="text-red-700 dark:text-red-400">{error}</div> : null}
      </div>
    )
  }

  if (!isAdmin) {
    return (
      <div className="mt-1.5 border-t border-border pt-1.5 text-xs text-muted-foreground">
        An admin connects a number to a measure. Until then it stays declared.
      </div>
    )
  }

  if (needed === null) {
    return (
      <div className="mt-1.5 border-t border-border pt-1.5 text-xs text-muted-foreground">
        This number is checked quarterly, and nothing here can measure a quarterly figure yet. It
        stays on this list rather than being shown as a monthly number.
      </div>
    )
  }

  if (!open) {
    return (
      <div className="mt-1.5 border-t border-border pt-1.5">
        <button
          type="button"
          className="flex items-center gap-1 text-xs font-medium text-[hsl(var(--brand-soft-foreground))] hover:underline"
          onClick={() => setOpen(true)}
        >
          <Link2 className="h-3.5 w-3.5" />
          Connect a measure
        </button>
      </div>
    )
  }

  return (
    <div className="mt-1.5 space-y-2 border-t border-border pt-2">
      {eligible.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          Nothing in the catalogue can answer at {number.cadence ?? 'monthly'} rhythm, so this
          number cannot be measured yet. Building a measure for it is a migration, not a setting.
        </p>
      ) : (
        <>
          <label className="block text-xs font-medium text-muted-foreground" htmlFor={`m-${number.metric_id}`}>
            Which measure stands for &quot;{number.name}&quot;?
          </label>
          <select
            id={`m-${number.metric_id}`}
            value={measureKey}
            onChange={(e) => setMeasureKey(e.target.value)}
            className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-xs text-foreground"
          >
            <option value="">Choose a measure</option>
            {eligible.map((m) => (
              <option key={m.measure_key} value={m.measure_key}>
                {m.label} ({m.scope === 'fleet' ? 'whole fleet' : 'this person'})
              </option>
            ))}
          </select>

          {chosen ? (
            <p className="rounded-md bg-muted px-2 py-1.5 text-xs text-muted-foreground">
              {chosen.source_note}
            </p>
          ) : null}

          <label className="block text-xs font-medium text-muted-foreground" htmlFor={`r-${number.metric_id}`}>
            Why is this a fair reading of the number?
          </label>
          <textarea
            id={`r-${number.metric_id}`}
            value={rationale}
            onChange={(e) => setRationale(e.target.value)}
            rows={2}
            placeholder="A binding without a reason is a number nobody agreed to."
            className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-xs text-foreground"
          />

          <div className="flex items-center gap-2">
            <Button
              size="sm"
              disabled={measureKey === '' || rationale.trim() === '' || bindFn.loading}
              onClick={() =>
                void run(() =>
                  bindFn.trigger(
                    {
                      metric_id: number.metric_id,
                      measure_key: measureKey,
                      rationale: rationale.trim(),
                    },
                    { skipCache: true },
                  ),
                )
              }
            >
              {bindFn.loading ? <Spinner className="mr-1.5 h-3.5 w-3.5" /> : null}
              Connect
            </Button>
            <Button size="sm" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <span className="text-xs text-muted-foreground">
              Then press Recompute to fill in the figures.
            </span>
          </div>
        </>
      )}
      {error ? <div className="text-xs text-red-700 dark:text-red-400">{error}</div> : null}
    </div>
  )
}
